import { useEffect } from 'react'
import { AttachUploadButton } from '~/components/chat/AttachUploadButton'
import { UploadCardsContainer } from '~/components/chat/UploadCardsContainer'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import useContextUpload from '~/hooks/useContextUpload'
import type { ContextUploadApiItem } from '~/types/contextUpload'

export const SUPPORTING_FILES_TOOLTIP =
  'Upload supporting files like docs, PDFs, images (PNG/JPG), or videos (MP4)'

export interface SupportingFilesUploadState {
  contextUploadData: ContextUploadApiItem[]
  hasPendingUploads: boolean
  hasFailedUploads: boolean
}

interface SupportingFilesUploadProps {
  onChange: (state: SupportingFilesUploadState) => void
  disabled?: boolean
}

export function SupportingFilesUpload({ onChange, disabled = false }: SupportingFilesUploadProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const workspaceUuid = currentWorkspace?.uuid

  const {
    contextUploads,
    handleFilesUpload,
    handleFolderUpload,
    handleRemoveContextUpload,
    getContextUploadData,
    hasPendingUploads
  } = useContextUpload(workspaceUuid)

  const hasFailedUploads = contextUploads.some(upload => upload.uploadStatus === 'error')

  useEffect(() => {
    onChange({
      contextUploadData: getContextUploadData(),
      hasPendingUploads,
      hasFailedUploads
    })
  }, [getContextUploadData, hasFailedUploads, hasPendingUploads, onChange])

  return (
    <div>
      <div className='label font-semibold text-xs text-default-700 mb-[10px] select-none'>
        Supporting Files:
      </div>

      {contextUploads.length > 0 && (
        <div className='mb-3'>
          <UploadCardsContainer uploads={contextUploads} onRemove={handleRemoveContextUpload} />
        </div>
      )}

      <div className='flex items-center gap-3'>
        <AttachUploadButton
          onFilesSelect={handleFilesUpload}
          onFolderSelect={handleFolderUpload}
          disabled={disabled}
          currentUploadCount={contextUploads.length}
          tooltipContent={SUPPORTING_FILES_TOOLTIP}
        />
        <div className='text-xs text-default-500 select-none'>{SUPPORTING_FILES_TOOLTIP}</div>
      </div>
    </div>
  )
}
